import { useCallback, useEffect, useRef, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import Lenis from "lenis";
import { AnimatePresence } from "framer-motion";
import { Toaster, toast } from "sonner";
import IntroScene, { shouldPlayIntro } from "./components/intro/IntroScene";
import { api } from "./lib/api";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { CartProvider } from "./context/CartContext";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import CartDrawer from "./components/CartDrawer";
import ChatWidget from "./components/ChatWidget";
import Home from "./pages/Home";
import Shop from "./pages/Shop";
import ProductDetail from "./pages/ProductDetail";
import Checkout from "./pages/Checkout";
import Auth from "./pages/Auth";
import Account from "./pages/Account";
import Admin from "./pages/Admin";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import About from "./pages/About";
import Terms from "./pages/Terms";
import Returns from "./pages/Returns";
import SizeGuide from "./pages/SizeGuide";

const ScrollManager = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    const lenis = new Lenis({ duration: 1.2, smoothWheel: true });
    window.__lenis = lenis;
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  useEffect(() => {
    if (window.__lenis) {
      window.__lenis.scrollTo(0, { immediate: true });
    } else {
      window.scrollTo(0, 0);
    }
  }, [pathname]);

  return null;
};

const AuthCallback = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { completeAuth } = useAuth();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const sessionId = new URLSearchParams(location.hash.slice(1)).get("session_id");
    api
      .post("/auth/google/session", { session_id: sessionId })
      .then((r) => {
        completeAuth(r.data);
        navigate("/account", { replace: true });
      })
      .catch(() => {
        toast.error("Google sign-in failed");
        navigate("/auth", { replace: true });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <div className="min-h-screen bg-[#0A0A0A]" data-testid="auth-callback" />;
};

const AppRouter = () => {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/shop" element={<Shop />} />
      <Route path="/product/:id" element={<ProductDetail />} />
      <Route path="/checkout" element={<Checkout />} />
      <Route path="/auth" element={<Auth />} />
      <Route path="/account" element={<Account />} />
      <Route path="/admin" element={<Admin />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/about" element={<About />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/returns" element={<Returns />} />
      <Route path="/size-guide" element={<SizeGuide />} />
    </Routes>
  );
};

function App() {
  const [intro, setIntro] = useState(() => shouldPlayIntro());
  const finishIntro = useCallback(() => setIntro(false), []);

  return (
    <div className="App min-h-screen bg-[#0A0A0A]">
      <div className="grain-overlay" />
      <AnimatePresence>{intro && <IntroScene onDone={finishIntro} />}</AnimatePresence>
      {!intro && (
      <BrowserRouter>
        <AuthProvider>
          <CartProvider>
            <ScrollManager />
            <Navbar />
            <CartDrawer />
            <main>
              <AppRouter />
            </main>
            <Footer />
            <ChatWidget />
            <Toaster
              theme="dark"
              toastOptions={{
                style: {
                  background: "#141414",
                  border: "1px solid rgba(212,175,55,0.3)",
                  color: "#F5F5F5",
                  borderRadius: 0,
                },
              }}
            />
          </CartProvider>
        </AuthProvider>
      </BrowserRouter>
      )}
    </div>
  );
}

export default App;
